/**
 * Сборка занятия режима переключения раскладок.
 *
 * Упражнение — цепочка фрагментов, где язык меняется на каждом шаге
 * (ТЗ FR-EN-24): границ смены раскладки в занятии не меньше заданного числа,
 * иначе режим тренирует не переключение, а набор.
 * Начинается она с языка профиля: пользователь входит в занятие в своей
 * раскладке, а не в чужой.
 *
 * Материал — обычные частотные слова обоих языков. Придумывать двуязычный
 * связный текст здесь не нужно и вредно: связный текст тренирует режим
 * «Предложения», а этот режим существует ради границы между фрагментами.
 *
 * Сборка детерминирована по seed: то же занятие воспроизводится в тестах
 * посимвольно (решение AD-012).
 */

import type { LanguageCode, MasteryUnit } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { getWords } from '@type2x/corpora';
import { createRng, type Rng } from '@type2x/exercise-generator';

import type {
  LayoutSwitchBoundary,
  LayoutSwitchExercise,
  LayoutSwitchPlan,
  LayoutSwitchSegment,
  LayoutSwitchSnapshot,
  SwitchDirection,
} from '../contracts/layout-switch.js';
import {
  HINT_LEVEL,
  LAYOUT_SWITCH_MODE,
  SEGMENTS_PER_EXERCISE,
  SWITCH_DIRECTIONS,
  SWITCH_LAYOUTS,
  SWITCH_UNIT_PREFIX,
  WORDS_PER_SEGMENT,
} from '../contracts/layout-switch.js';

/**
 * Сколько слов языка держать в пуле.
 *
 * С запасом относительно упражнения, и запас этот не лишний: пул берётся с
 * головы частотного списка и не меняется от занятия к занятию, поэтому узкий
 * пул означал бы одни и те же слова каждый раз (замер этапа ABM). Сто двадцать
 * частотных слов — всё ещё частотные слова, а разных фрагментов из них вдвое
 * больше.
 */
const POOL_SIZE = 120;

/** Слова, из которых собираются фрагменты языка. */
function wordPool(language: LanguageCode): string[] {
  return getWords(language, { minLength: 3, maxLength: 8, limit: POOL_SIZE }).map(
    (word) => word.text,
  );
}

/** Идентификатор учебной единицы перехода по его направлению. */
export function switchUnitId(directionId: string): string {
  return `${SWITCH_UNIT_PREFIX}${directionId}`;
}

/** Направление перехода между двумя языками, либо null, если они совпадают. */
export function directionBetween(from: LanguageCode, to: LanguageCode): SwitchDirection | null {
  return SWITCH_DIRECTIONS.find((d) => d.from === from && d.to === to) ?? null;
}

/**
 * Учебная единица перехода.
 *
 * Раскладка единицы — та, в которую переключаются: движение принадлежит ей,
 * а не той, из которой уходят. Частота в реальном языке нулевая — переход не
 * встречается в частотном списке, он вообще не текст.
 */
export function switchUnit(
  snapshot: LayoutSwitchSnapshot,
  direction: SwitchDirection,
): MasteryUnit {
  return {
    id: toMasteryUnitId(switchUnitId(direction.id)),
    kind: 'pattern',
    content: `${direction.from} → ${direction.to}`,
    language: snapshot.language,
    layoutId: SWITCH_LAYOUTS[direction.to],
    realWorldFrequency: 0,
  };
}

/** Языки фрагментов по порядку: чередование, начиная с языка профиля. */
export function segmentLanguages(start: LanguageCode, count: number): LanguageCode[] {
  const other: LanguageCode = start === 'ru' ? 'en' : 'ru';
  return Array.from({ length: count }, (_, index) => (index % 2 === 0 ? start : other));
}

/** Собирает фрагменты и запоминает, где начинается каждый. */
function buildSegments(rng: Rng, start: LanguageCode): LayoutSwitchSegment[] {
  const pools: Record<LanguageCode, string[]> = { ru: wordPool('ru'), en: wordPool('en') };
  const segments: LayoutSwitchSegment[] = [];
  let position = 0;

  for (const language of segmentLanguages(start, SEGMENTS_PER_EXERCISE)) {
    const words = Array.from({ length: WORDS_PER_SEGMENT }, () => rng.pick(pools[language]));
    const text = words.join(' ');

    segments.push({ language, layoutId: SWITCH_LAYOUTS[language], text, start: position });

    // Фрагменты соединяются одним пробелом: он и занимает одну позицию.
    position += [...text].length + 1;
  }

  return segments;
}

/**
 * Границы смены раскладки.
 *
 * Граница относится к первому символу фрагмента после неё: именно перед ним
 * пользователь переключает раскладку, и интервал перед этим нажатием — цена
 * перехода.
 */
export function boundariesOf(segments: readonly LayoutSwitchSegment[]): LayoutSwitchBoundary[] {
  const boundaries: LayoutSwitchBoundary[] = [];

  for (const [index, segment] of segments.entries()) {
    const previous = segments[index - 1];
    if (previous === undefined) continue;

    const direction = directionBetween(previous.language, segment.language);
    if (direction === null) continue;

    boundaries.push({ direction, position: segment.start });
  }

  return boundaries;
}

/**
 * Строит занятие режима.
 *
 * @param snapshot состояние навыка;
 * @param seed зерно генератора: одно и то же занятие воспроизводимо.
 */
export function buildLayoutSwitchPlan(
  snapshot: LayoutSwitchSnapshot,
  seed: string,
): LayoutSwitchPlan {
  const rng = createRng(seed);
  const segments = buildSegments(rng, snapshot.language);
  const boundaries = boundariesOf(segments);

  const exercise: LayoutSwitchExercise = {
    seed,
    segments,
    boundaries,
    text: segments.map((segment) => segment.text).join(' '),
  };

  // Единицы занятия — только те переходы, что в нём действительно есть: итог
  // не должен выдавать за тренировку направление, которое не встретилось.
  const present = new Map<string, SwitchDirection>();
  for (const boundary of boundaries) present.set(boundary.direction.id, boundary.direction);

  return {
    mode: LAYOUT_SWITCH_MODE,
    language: snapshot.language,
    layoutId: SWITCH_LAYOUTS[snapshot.language],
    units: [...present.values()].map((direction) => switchUnit(snapshot, direction)),
    exercise,
    hintLevel: HINT_LEVEL,
  };
}
